import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  VStack,
  Text,
  Spinner,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import showToast from "hooks/useCustomToast";
import DocModal from "../../components/eva/DocModal";
import EvaLeftPanel from "../../components/eva/EvaLeftPanel";
import EvaRightPanel from "../../components/eva/EvaRightPanel";
import { AgentConfig, ToolConfig, PromptConfig } from "build-ts/agent_config";
import { getAgentById, getToolsList, updateAgent, getSystemPromptDocumentation } from "services/EvaServices";

const EvaDetailPage: React.FC = () => {
  const { agent_id } = useParams<{ agent_id: string }>();

  const [promptEditor, setPromptEditor] = useState("");
  const [agentConfig, setAgentConfig] = useState<AgentConfig>({
    id: "",
    name: "",
    agent_type: undefined,
    description: "",
    is_active: false,
    analysis_flag: JSON.stringify({
      include_tree_insights_in_analysis: false,
    }),
    version: 1,
    prompt_config: {
      model_name: "gpt-4o",
      model_type: "openai",
      temperature: 0,
      id: "",
      name: "",
      philosophy: "",
      instructions: "",
    },
    tools: [],
  });

  // Separate prompt configuration state
  const [promptConfig, setPromptConfig] = useState<PromptConfig>({
    id: "",
    name: "",
    model_name: "gpt-4o",
    model_type: "openai",
    temperature: 0,
    philosophy: "",
    instructions: "",
    is_active: false,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [availableTools, setAvailableTools] = useState<ToolConfig[]>([]);
  const [selectedTools, setSelectedTools] = useState<ToolConfig[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [originalAgentConfig, setOriginalAgentConfig] = useState<AgentConfig | null>(null);
  const [originalPromptConfig, setOriginalPromptConfig] = useState<PromptConfig | null>(null);
  const [originalPromptEditor, setOriginalPromptEditor] = useState("");
  const [originalSelectedTools, setOriginalSelectedTools] = useState<ToolConfig[]>([]);

  // Documentation modal states
  const [availableVariables, setAvailableVariables] = useState<Record<string, any[]>>({});
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const { isOpen: isDocsOpen, onOpen: onDocsOpen, onClose: onDocsClose } = useDisclosure();

  const fetchToolsList = async () => {
    try {
      const params = new URLSearchParams({
        limit: "100",
        offset: "0",
        sort_by: "name",
        sort_order: "asc",
      });

      const data = await getToolsList(params);
      return data.tools || [];
    } catch (error) {
      console.error("Error fetching tools list:", error);
      showToast("Failed to fetch tools list", "", "error");
      throw error;
    }
  };

  // API call to fetch system prompt documentation
  const fetchSystemPromptDocumentation = async () => {
    try {
      setIsLoadingDoc(true);
      const data = await getSystemPromptDocumentation();
      // Set the grouped variables directly from the API response
      if (data) {
        setAvailableVariables(data);
      } else {
        setAvailableVariables({});
      }
    } catch (error) {
      console.error("Error fetching system prompt documentation:", error);
      showToast("Failed to fetch documentation", "", "error");
      setAvailableVariables({});
    } finally {
      setIsLoadingDoc(false);
    }
  };

  const handleViewDocs = async () => {
    onDocsOpen();
    // Fetch documentation when modal opens
    await fetchSystemPromptDocumentation();
  };

  // Load agent and prompt data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!agent_id) {
        showToast("No agent ID provided", "", "error");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch agent data and tools list in parallel
        const [agentData, toolsData] = await Promise.all([
          getAgentById(agent_id),
          fetchToolsList(),
        ]);

        // Check if agent data was successfully fetched
        if (!agentData) {
          showToast("Error", "Failed to load agent data", "error");
          return;
        }

        const agentInitialState = {
          id: agentData.id || "",
          name: agentData.name.trim() || "",
          agent_type: agentData.agent_type || undefined,
          description: agentData.description.trim() || "",
          is_active: agentData.is_active ?? false,
          analysis_flag: agentData.analysis_flag || JSON.stringify({
            include_tree_insights_in_analysis: false,
          }),
          version: agentData.version || 1,
          prompt_config: {
            model_name: agentData.prompt_config?.model_name || "gpt-4o",
            model_type: agentData.prompt_config?.model_type || "openai",
            temperature: agentData.prompt_config?.temperature ?? 0,
            id: agentData.prompt_config?.id || "",
            name: agentData.prompt_config?.name || "",
            philosophy: agentData.prompt_config?.philosophy.trim() || "",
            instructions: agentData.prompt_config?.instructions.trim() || "",
            created_date: agentData.prompt_config?.created_date || 0,
            modified_date: agentData.prompt_config?.modified_date || 0,
          },
          tools: agentData.tools || [],
        };
        setAgentConfig({
          ...agentInitialState
        });

        // Initialize separate prompt config state
        const promptInitialState = {
          id: agentData.prompt_config?.id || "",
          name: agentData.prompt_config?.name || "",
          model_name: agentData.prompt_config?.model_name || "gpt-4o",
          model_type: agentData.prompt_config?.model_type || "openai",
          temperature: agentData.prompt_config?.temperature ?? 0,
          philosophy: agentData.prompt_config?.philosophy || "",
          instructions: agentData.prompt_config?.instructions || "",
          is_active: agentData.prompt_config?.is_active ?? false,
          created_date: agentData.prompt_config?.created_date || 0,
          modified_date: agentData.prompt_config?.modified_date || 0,
        };
        setPromptConfig(promptInitialState);

        // Initialize selectedTools from agent data
        setSelectedTools(agentData.tools?.map((tool: ToolConfig) => tool) || []);

        // Set prompt editor from template if available
        setPromptEditor(agentData.prompt_config?.philosophy || "");

        setAvailableTools(toolsData);

        // Store original state for cancel functionality
        setOriginalAgentConfig({
          ...agentInitialState
        });
        setOriginalPromptConfig(promptInitialState);
        setOriginalPromptEditor(agentData.prompt_config?.philosophy || "");
        setOriginalSelectedTools(agentData.tools?.map((tool: ToolConfig) => tool) || []);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent_id]);

  const handleConfigChange = (field: string, value: any) => {
    setAgentConfig(prev => {
      // Handle nested field updates (e.g., "analysis_flag.include_tree_insights_in_analysis")
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "analysis_flag") {
          const currentFlag = typeof prev.analysis_flag === "string"
            ? JSON.parse(prev.analysis_flag)
            : prev.analysis_flag;
          return {
            ...prev,
            analysis_flag: JSON.stringify({
              ...currentFlag,
              [childField]: value
            })
          };
        }
        if (parentField === "prompt_config") {
          return {
            ...prev,
            prompt_config: {
              ...prev.prompt_config,
              [childField]: value
            }
          };
        }
      }

      // Handle template updates - also update promptEditor
      if (field === "template") {
        setPromptEditor(value);
        return {
          ...prev,
          prompt_config: {
            ...prev.prompt_config,
            philosophy: value
          }
        };
      }

      // Handle analysis_flag updates
      if (field === "analysis_flag") {
        return {
          ...prev,
          analysis_flag: typeof value === "string" ? value : JSON.stringify(value)
        };
      }

      // Handle other direct field updates
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleToolsChange = (selectedToolsArray: ToolConfig[]) => {
    setSelectedTools(selectedToolsArray);
    setAgentConfig(prev => ({
      ...prev,
      tools: selectedToolsArray
    }));
  };

  const handlePromptEditorChange = (value: string) => {
    setPromptEditor(value);
    setPromptConfig(prev => ({
      ...prev,
      philosophy: value
    }));
  };

  const handlePromptConfigChange = (field: string, value: any) => {
    setPromptConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleEdit = () => {
    // Store original state
    setOriginalAgentConfig({ ...agentConfig });
    setOriginalPromptConfig({ ...promptConfig });
    setOriginalPromptEditor(promptEditor);
    setOriginalSelectedTools([...selectedTools]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Restore original state
    if (originalAgentConfig) {
      setAgentConfig(originalAgentConfig);
    }
    if (originalPromptConfig) {
      setPromptConfig(originalPromptConfig);
    }
    setPromptEditor(originalPromptEditor);
    setSelectedTools(originalSelectedTools);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const result = await updateAgent(agent_id, agentConfig, selectedTools);

    // Only update local state if the update was successful
    if (result) {
      // Update the local state with the new prompt name (generated from agent name)
      setAgentConfig(prev => ({
        ...prev,
      }));
      setIsEditing(false);
    }
  };

  if (isLoading) {
    return (
      <Box className="eva-container eva-small-scrollbar" display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading agent data...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box className="eva-container eva-small-scrollbar">
      <Flex className="eva-main-layout">
        {/* Left Panel - Agent Configuration */}
        <EvaLeftPanel
          agentConfig={agentConfig}
          availableTools={availableTools}
          onConfigChange={handleConfigChange}
          onToolsChange={handleToolsChange}
          isEditing={isEditing}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onSave={handleSave}
          onViewDocs={handleViewDocs}
        />

        {/* Right Panel - Prompt Editor */}
        <EvaRightPanel
          agentConfig={agentConfig}
          promptConfig={promptConfig}
          promptEditor={promptEditor}
          onPromptEditorChange={handlePromptEditorChange}
          onPromptConfigChange={handlePromptConfigChange}
          analysis_flag={agentConfig.analysis_flag || ""}
          onConfigChange={handleConfigChange}
        />
      </Flex>

      {/* Variables Documentation Modal */}
      <DocModal
        isDocsOpen={isDocsOpen}
        onDocsClose={onDocsClose}
        isLoadingDoc={isLoadingDoc}
        availableVariables={availableVariables}
      />
    </Box>
  );
};

export default EvaDetailPage;
